'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import countryCallingCodes from 'country-calling-code';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export interface ICountryCodeDropdownOption {
  /**
   * The calling code of the country.
   */
  code: string;
  isoCode2: string;
}

interface ICountryCodeDropdownProps {
  /**
   * The default selected country calling code option.
   */
  defaultOption: ICountryCodeDropdownOption;
  /**
   * A callback function that will be executed when the country calling code
   * dropdown is changed.
   */
  onChange: (selected: ICountryCodeDropdownOption) => void;
}

/**
 * -----------------------------------------------------------------------------
 * It renders the country calling code dropdown and also has a option to set the
 * default country calling code to the dropdown.
 */
export function CountryCodeDropdown({
  defaultOption,
  onChange,
}: Readonly<ICountryCodeDropdownProps>) {
  const [selected, setSelected] = useState(defaultOption);

  /**
   * The options array will contain the country calling code which will be used to
   * show in the dropdown.
   * - This first removes the `-` hyphen from the codes, sorts them and adds a `+`.
   */
  const callingCodes = useMemo(
    () =>
      countryCallingCodes
        .map((item) => ({
          ...item,
          countryCode: item.countryCodes[0]?.replace(/-/g, '') || '0',
        }))
        .sort(
          (a, b) => parseInt(a.countryCode, 10) - parseInt(b.countryCode, 10)
        )
        .map((item) => ({
          ...item,
          code: `+${item.countryCode}`,
        })),
    []
  );

  useEffect(() => {
    onChange(selected);
  }, [onChange, selected]);

  return (
    <Listbox
      as="div"
      className="relative mt-1.5"
      onChange={setSelected}
      value={selected}
    >
      <ListboxButton
        className="
          flex w-full items-center justify-center gap-2 rounded-lg rounded-e-none
          border-0 border-e-0 bg-white px-2 py-2.5 text-sm
          font-light shadow-none
        "
      >
        <div className="grow text-start">{selected?.code}</div>
        <ChevronDownIcon className="h-4 w-4" />
      </ListboxButton>
      <ListboxOptions
        className="
          absolute z-20 max-h-48 w-full overflow-y-auto shadow
        "
      >
        {callingCodes.map((item) => (
          <>
            <ListboxOption
              key={item.code}
              className="
                data-focus:bg-green-100 max-h-36 cursor-pointer bg-white px-2 py-2 text-sm
                shadow-none focus:border-gray-200
              "
              value={item}
            >
              {item.code}
            </ListboxOption>
            <hr />
          </>
        ))}
      </ListboxOptions>
    </Listbox>
  );
}
